import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";

// Real clubs — the ones who trusted Nono. Taglines come from i18n (t.trust.taglines).
const CLUBS = [
  {
    id: "lru",
    name: "Lille Ring United",
    logo: "/img/logo-blanc.webp",
    // White-on-transparent → shows natively on dark tile.
    filter: "none",
  },
  {
    id: "bcr",
    name: "Boxing Club Roubaix",
    logo: "/img/logo-images.webp",
    // Raster logo on white paper → invert + hue-rotate keeps original colours while
    // turning the white background black so it blends into the dark tile.
    filter: "invert(1) hue-rotate(180deg)",
  },
  {
    id: "bca",
    name: "Boxing Club Armentières",
    logo: "/img/logo-mtp.webp",
    filter: "none",
  },
  {
    id: "ceintures",
    name: "Les Ceintures — MTP Boxing",
    logo: "/img/logo-chatgpt2.webp",
    filter: "none",
  },
  {
    id: "wbc",
    name: "WBC",
    logo: "/img/logo-wbc.webp",
    // Official logo on white paper → same treatment as Roubaix (white bg → black, colours kept).
    filter: "invert(1) hue-rotate(180deg)",
  },
  {
    id: "mbg",
    name: "Metz Boxing Gym",
    logo: "/img/logo-metz-boxing-gym.webp",
    // Black-on-transparent → invert to white so it reads on the dark tile.
    filter: "invert(1)",
  },
];

const easeOut = [0.25, 0.4, 0.25, 1];

function LogoBadge({ club, tagline }) {
  return (
    <div className="group shrink-0 flex flex-col items-center justify-center gap-3 border border-line hover:border-moss px-8 py-6 min-w-[240px] min-h-[150px] transition-colors duration-500 bg-ink">
      <div className="flex items-center justify-center h-16 w-full">
        <img
          src={club.logo}
          alt={club.name}
          loading="lazy"
          draggable={false}
          style={{ filter: club.filter || "none" }}
          className="max-h-16 max-w-[160px] object-contain"
        />
      </div>
      <div className="text-center">
        <p translate="no" className="notranslate text-[11px] tracking-[0.28em] uppercase text-bone/85">{club.name}</p>
        <p className="text-[9px] tracking-[0.32em] uppercase mt-1 text-ash">
          {tagline}
        </p>
      </div>
    </div>
  );
}

function TestimonialCard({ t, i }) {
  return (
    <motion.figure
      data-testid={`testimonial-${t.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: i * 0.1, ease: easeOut }}
      className="relative flex flex-col justify-between h-full border border-line hover:border-moss bg-surface/40 p-8 md:p-10 transition-colors duration-500"
    >
      <Quote
        size={28}
        strokeWidth={1.2}
        className="text-leaf/70 mb-6"
        aria-hidden
      />
      <blockquote className="font-serif text-bone text-xl md:text-2xl leading-snug">
        “{t.quote}”
      </blockquote>
      <figcaption className="mt-8 pt-6 border-t border-line">
        <p translate="no" className="notranslate text-bone text-sm">{t.author}</p>
        <p className="text-[10px] tracking-[0.32em] uppercase text-ash mt-1">
          {t.role}
        </p>
      </figcaption>
    </motion.figure>
  );
}

export default function Testimonials({ t }) {
  return (
    <section
      id="trust"
      data-testid="testimonials-section"
      className="relative bg-ink border-t border-line py-24 md:py-36 overflow-hidden"
    >
      <div className="relative px-6 md:px-12 lg:px-20">
        {/* Header */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 mb-14 md:mb-20">
          <div className="md:col-span-2">
            <p className="font-display text-6xl md:text-7xl leading-none text-leaf/80">05</p>
            <p className="mt-3 text-[10px] tracking-[0.4em] uppercase text-ash">
              {t.trust.chapter}
            </p>
          </div>
          <div className="md:col-span-6">
            <motion.h2
              data-testid="testimonials-title"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease: easeOut }}
              className="font-serif font-light text-bone text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight"
            >
              {t.trust.title}
              <br />
              <span className="italic">{t.trust.title2}</span>
            </motion.h2>
          </div>
          <div className="md:col-span-4 md:pt-4">
            <p className="text-ash text-base leading-relaxed">{t.trust.intro}</p>
          </div>
        </div>

        {/* Club logos — infinite scrolling marquee (real clubs) */}
        <div
          data-testid="clubs-marquee"
          className="relative -mx-6 md:-mx-12 lg:-mx-20 mb-20 md:mb-28 overflow-hidden marquee-mask"
        >
          <div className="flex marquee-track-slow gap-4 md:gap-5 py-2">
            {[...CLUBS, ...CLUBS, ...CLUBS, ...CLUBS, ...CLUBS, ...CLUBS].map((c, i) => (
              <LogoBadge key={`${c.id}-${i}`} club={c} tagline={t.trust.taglines[c.id]} />
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 md:gap-6">
          {t.trust.quotes.map((tst, i) => (
            <TestimonialCard key={tst.id} t={tst} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
